import { U } from "./accessors";
import type { InfiniteWarState, IwTeam, IwTileOwner } from "./types";

/** Living (non-veil) unit counts whose feet sit inside the tile AABB. */
export function tileLivingPresence(
  state: InfiniteWarState,
  col: number,
  row: number,
): { celestial: number; death: number } {
  const f = U.field;
  const tileW = f.w / f.cols;
  const tileH = f.h / f.rows;
  const x0 = f.x + col * tileW;
  const y0 = f.y + row * tileH;
  const x1 = x0 + tileW;
  const y1 = y0 + tileH;
  let celestial = 0;
  let death = 0;
  for (const unit of state.units) {
    if (unit.phase !== "alive") continue; // Soul residue never counts
    if (unit.x < x0 || unit.x >= x1 || unit.y < y0 || unit.y >= y1) continue;
    if (unit.team === "south") celestial += 1;
    else death += 1;
  }
  return { celestial, death };
}

export function tileOwner(
  state: InfiniteWarState,
  col: number,
  row: number,
): IwTileOwner {
  return state.tileOwners[row]?.[col] ?? null;
}

/**
 * Sticky dominion: empty tiles keep last owner; sole living faction claims/flips;
 * contested (both alive) holds current owner; Soul residue never writes.
 */
export function updateTileOwners(state: InfiniteWarState): void {
  const { cols, rows } = U.field;
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const { celestial, death } = tileLivingPresence(state, col, row);
      const soleSouth = celestial > 0 && death === 0;
      const soleNorth = death > 0 && celestial === 0;
      if (soleSouth) state.tileOwners[row]![col] = "south";
      else if (soleNorth) state.tileOwners[row]![col] = "north";
      // else empty or contested → sticky (or remain null if never claimed)
    }
  }
}

/** True when both living factions share the tile (owner sticky; presentation may read contested). */
export function tileContested(
  state: InfiniteWarState,
  col: number,
  row: number,
): boolean {
  const { celestial, death } = tileLivingPresence(state, col, row);
  return celestial > 0 && death > 0;
}

export function enemiesInPressHalf(state: InfiniteWarState, team: IwTeam): number {
  const midY = U.field.y + U.field.h * 0.5;
  return state.units.filter((u) => {
    if (u.phase !== "alive" || u.team === team) return false;
    return team === "south" ? u.y <= midY : u.y >= midY;
  }).length;
}
